import type { PerAppCostBreakdown } from "@/api/calculate";
import {
  parseDecimalString,
  roundUtilRatioToDisplayPercent,
} from "@/utils/cost-display";

/**
 * POC compare-card footer via T2:
 * sum per-app utilization ratios for checked apps. Valid while every app in one
 * calculate response shares the same machineHoursAvailablePerYear / workerHoursAvailablePerYear
 * (same user assumptions + model OEE for that product).
 */
export type SelectedAppsTotals = {
  machineUtilizationPercent: number | null;
  workerUtilizationPercent: number | null;
  requiredMachines: number | null;
  requiredOperators: number | null;
};

export function computeSelectedAppsTotals(
  perApp: PerAppCostBreakdown[],
  selectedApplicationIds: string[],
): SelectedAppsTotals | null {
  if (selectedApplicationIds.length === 0) return null;

  const byId = new Map(
    perApp
      .filter((r) => r.applicationId)
      .map((r) => [r.applicationId!, r] as const),
  );

  let totMu = 0;
  let totOu = 0;
  let displayMachinePct = 0;
  let displayWorkerPct = 0;
  let anyMachine = false;
  let anyWorker = false;

  for (const appId of selectedApplicationIds) {
    const row = byId.get(appId);
    if (!row) continue;
    const mu = parseDecimalString(row.machineUtilization);
    const ou = parseDecimalString(row.workerUtilization);
    if (mu != null) {
      totMu += mu;
      displayMachinePct += roundUtilRatioToDisplayPercent(mu);
      anyMachine = true;
    }
    if (ou != null) {
      totOu += ou;
      displayWorkerPct += roundUtilRatioToDisplayPercent(ou);
      anyWorker = true;
    }
  }

  if (!anyMachine && !anyWorker) return null;

  // Footer % = sum of each per-app line’s displayed whole % (17 + 26 → 43), not
  // round(sum(raw ratios)×100) which can be 42 when raw values are e.g. 0.1667 + 0.2551.
  // Counts follow svc-10 (aggregate-comparison-kpis): ceil of the summed raw ratios, min 1.
  return {
    machineUtilizationPercent: anyMachine ? displayMachinePct : null,
    workerUtilizationPercent: anyWorker ? displayWorkerPct : null,
    requiredMachines: anyMachine ? requiredCount(totMu) : null,
    requiredOperators: anyWorker ? requiredCount(totOu) : null,
  };
}

function requiredCount(utilization: number): number {
  return utilization > 0 ? Math.max(1, Math.ceil(utilization)) : 1;
}

export function formatTotalsUtilPercent(value: number | null): string {
  if (value == null) return "—";
  return `${value}%`;
}
